import React from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import { isMobile, isIOS, isChrome } from 'react-device-detect';

import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Dialog from '@material-ui/core/Dialog';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import CameraIcon from '@material-ui/icons/PhotoCameraOutlined';
import AddPhotoAltIcon from '@material-ui/icons/AddPhotoAlternateOutlined';
import BrokenImageIcon from '@material-ui/icons/BrokenImageOutlined';
import CloseIcon from '@material-ui/icons/Close';
import ButtonBase from '@material-ui/core/ButtonBase';
import VisuallyHidden from '../../../../components/VisuallyHidden/VisuallyHidden';
import FabWithText from '../../../../components/FabWithText/FabWithText';
import BaseDialog from '../../../../components/BaseDialog/BaseDialog';
import ActionButton from '../../../../components/ActionButton/ActionButton';
import { getString } from '../../../../helpers/stringHelpers';
import objectFitImages from 'object-fit-images';
import moment from 'moment';

import { withStyles } from '@material-ui/core/styles';
import styles from './styles.js';

const stringsComponent = 'photoField';

class PhotoField extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      previewOpen: false,
      previewFile: null,
      downloadPhotoOpen: false,
    };

    this.toggleDialog = this.toggleDialog.bind(this);
    this.toggleDownloadDialog = this.toggleDownloadDialog.bind(this);

    this.cameraDropzoneRef = React.createRef();
    this.browseDropzoneRef = React.createRef();
  }

  toggleDialog(file = null) {
    const { previewOpen } = this.state;
    this.setState({ previewOpen: !previewOpen, previewFile: file });
  }

  toggleDownloadDialog(file = null) {
    const { downloadPhotoOpen } = this.state;
    this.setState({ downloadPhotoOpen: !downloadPhotoOpen });
  }

  makeThumbs() {
    const { classes, files, removeAction } = this.props;

    return files.map((file, index) => (
      <Card key={index} className={classes.photoCard}>
        <CardContent className={classes.thumb}>
          <div className={classes.thumbInner}>
            <ButtonBase
              className={classes.thumbInner}
              onClick={() => this.toggleDialog(file.preview)}
              autoFocus={true}
            >
              <img
                src={file.preview}
                className={classes.img}
                alt={getString(stringsComponent, 'IMAGE_ALT_TEXT')}
              />
            </ButtonBase>
          </div>
          <Button className={classes.removeAction} onClick={removeAction}>
            <VisuallyHidden
              body={getString(stringsComponent, 'DELETE_LABEL')}
            />
            <DeleteIcon />
          </Button>
        </CardContent>
      </Card>
    ));
  }

  makeDropzone(accept, ref, label, capture = null) {
    const { classes, onDrop, files, photoProcessing } = this.props;

    return (
      <Dropzone
        accept={accept}
        onDrop={
          capture
            ? (files) => {
                onDrop(files);
                this.toggleDownloadDialog();
              }
            : onDrop
        }
        ref={ref}
      >
        {({ getRootProps, getInputProps }) => (
          <section className={photoProcessing ? classes.hidden : ''}>
            <div
              {...getRootProps({
                className: `${classes.fieldContainer} dropzone`,
              })}
            >
              <div className={classes.buttonContainer}>
                <input
                  {...getInputProps({
                    multiple: false,
                    capture: capture,
                  })}
                />
                <FabWithText
                  label={label}
                  icon={capture == 'camera' ? CameraIcon : AddPhotoAltIcon}
                  action={() => {}}
                  ariaLabel={
                    capture == 'camera' ? 'Take a photo' : 'Browse files'
                  }
                  wrap={true}
                />
              </div>
            </div>
          </section>
        )}
      </Dropzone>
    );
  }

  makePreviewDialog() {
    const { classes } = this.props;
    const { previewOpen, previewFile } = this.state;
    return (
      <Dialog
        open={previewOpen}
        onClose={this.toggleDialog}
        classes={{ paperScrollPaper: classes.previewDialog }}
      >
        <div className={classes.closeIcon}>
          <ActionButton
            icon={CloseIcon}
            label={'Close'}
            action={this.toggleDialog}
          />
        </div>
        <div className={classes.previewImageContainer}>
          <img
            className={classes.previewImage}
            src={typeof previewFile === 'string' ? previewFile : ''}
            alt={getString(stringsComponent, 'IMAGE_ALT_TEXT')}
          />
        </div>
      </Dialog>
    );
  }

  makeDownloadDialog() {
    // Only show this dialog if brosers supports 'download' attr. for 'a' tags.
    // See MWD-1000
    // 'download' attr. also doesn't work in Chrome in IOS so disable it there too.
    // See MWD-1033
    if ('download' in document.createElement('a') && !(isIOS && isChrome)) {
      const { downloadPhotoOpen } = this.state;
      const { downloadLink } = this.props;
      return (
        <BaseDialog
          open={downloadPhotoOpen}
          icon={CameraIcon}
          buttonDownloadLink={downloadLink}
          buttonAction={this.toggleDownloadDialog}
          buttonLabel={getString(stringsComponent, 'SAVE_PHOTO_BUTTON_LABEL')}
          buttonDownloadFileName={`myko-maintenance-photo-${moment().format(
            'YYYY-MM-DDTHHmmss'
          )}`}
          secondaryButtonAction={this.toggleDownloadDialog}
          secondaryButtonLabel={getString(
            stringsComponent,
            'CLOSE_BUTTON_LABEL'
          )}
          title={getString(stringsComponent, 'SAVE_PHOTO_DIALOG_TITLE')}
          content={getString(stringsComponent, 'SAVE_PHOTO_DIALOG_CONTENT')}
        />
      );
    }
  }

  render() {
    const { classes, photoProcessing, files, error, downloadLink } = this.props;
    const hasPhoto = files.length > 0;
    objectFitImages();

    return (
      <div className={classes.root}>
        {error && (
          <Card>
            <CardContent className={classes.photoCard}>
              <BrokenImageIcon className={classes.errorIcon} />
              <Typography variant='h4' component='h4' gutterBottom={true}>
                {getString(stringsComponent, 'ERROR_HEADING')}
              </Typography>
              <Typography>{error}</Typography>
            </CardContent>
          </Card>
        )}
        {hasPhoto && (
          <div className={classes.thumbsContainer}>{this.makeThumbs()}</div>
        )}
        <div>
          {isMobile &&
            // Camera capture only seems to work consistantly with 'image/*'.
            // Files are still validated server side.
            this.makeDropzone(
              'image/*',
              this.cameraDropzoneRef,
              hasPhoto
                ? getString(stringsComponent, 'CAMERA_DIFFERENT_LABEL')
                : getString(stringsComponent, 'CAMERA_LABEL'),
              'camera'
            )}
          {!hasPhoto &&
            this.makeDropzone(
              'image/png,image/jpeg',
              this.browseDropzoneRef,
              isMobile
                ? hasPhoto
                  ? getString(stringsComponent, 'UPLOAD_DIFFERENT_LABEL')
                  : getString(stringsComponent, 'UPLOAD_LABEL')
                : getString(stringsComponent, 'BROWSE_LABEL')
            )}
        </div>
        {photoProcessing && (
          <div>
            <LinearProgress className={classes.progress} />
          </div>
        )}
        {this.makePreviewDialog()}
        {downloadLink && this.makeDownloadDialog()}
      </div>
    );
  }
}

PhotoField.propTypes = {
  classes: PropTypes.object.isRequired,
  files: PropTypes.array,
  onDrop: PropTypes.func.isRequired,
  removeAction: PropTypes.func.isRequired,
  photoProcessing: PropTypes.bool.isRequired,
  error: PropTypes.string,
  downloadLink: PropTypes.string,
};

PhotoField.defaultProps = {
  files: [],
  error: '',
  downloadLink: null,
};

export default withStyles(styles)(PhotoField);
